import React, {useEffect, useState, useCallback} from 'react';
import {SongBackupService} from '../../services/song-backup-service';
import {FaDownload, FaTrash, FaHistory, FaFileAlt} from 'react-icons/fa';
import './BackupManagerComponent.css';
import {useWorkspaceReadOnly} from '../../../../shared/hooks/useWorkspaceReadOnly';
import {assertWorkspaceWritable} from '../../../../shared/services/workspaceAccess';
import {getWorkspaceId} from '../../../../shared/services/dbUtils';

const formatSize = bytes => {
  if (!bytes) return '0 B';
  const unit = Math.min(3, Math.floor(Math.log(bytes) / Math.log(1024)));
  return (bytes / 1024 ** unit).toLocaleString('pl-PL', {maximumFractionDigits: 2}) + ' ' + ['B', 'KB', 'MB', 'GB'][unit];
};
const formatDate = date => new Date(date).toLocaleString('pl-PL');

const BackupManagerComponent = ({onBackupRestore, busy = false}) => {
  const isReadOnly = useWorkspaceReadOnly();
  const [backups, setBackups] = useState([]);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const loadBackups = useCallback(async () => {
    try { setBackups(await SongBackupService.getStoredBackups()); }
    catch { setError('Nie udało się wczytać kopii.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    loadBackups();
    return SongBackupService.observeBackupsChanges(loadBackups);
  }, [loadBackups]);

  const handleDownload = backup => {
    try {
      SongBackupService.downloadBlob(SongBackupService.base64ToBlob(backup.data, 'application/zip'),
        `Chwała_net_backup_${backup.date.replace(/[-:]/g, '').slice(0, 15)}.zip`);
    } catch { setError('Nie udało się pobrać kopii.'); }
  };
  const handleRestore = async () => {
    if (!selectedBackup || isReadOnly || working || busy) return;
    setWorking(true); setError('');
    try {
      assertWorkspaceWritable(getWorkspaceId());
      const files = await SongBackupService.restoreBackup(selectedBackup.id);
      if (!files.length) throw new Error('Kopia nie zawiera pieśni w formacie TXT.');
      await onBackupRestore(files);
      setSelectedBackup(null);
    } catch (failure) { setError(failure.message || 'Nie udało się przywrócić kopii.'); }
    finally { setWorking(false); }
  };
  const handleDelete = async backup => {
    if (!window.confirm('Usunąć tę kopię zapasową? Pieśni pozostaną bez zmian.')) return;
    setWorking(true); setError('');
    try {
      if (!await SongBackupService.deleteBackup(backup.id)) throw new Error('Nie udało się usunąć kopii.');
      await loadBackups();
    } catch (failure) { setError(failure.message); }
    finally { setWorking(false); }
  };

  return <section className={'backup-container' + (isReadOnly ? ' backup-readonly' : '')} aria-label="Zapisane kopie">
    <h2 className="backup-list-title">Zapisane kopie <span>· {backups.length}</span></h2>
    {error && <p role="alert" className="backup-feedback backup-error">{error}</p>}
    {loading ? <p role="status" className="backup-empty">Ładowanie…</p> : !backups.length ?
      <p className="backup-empty">Brak zapisanych kopii zapasowych.</p> : <>
        <div className="backup-list-columns" aria-hidden="true"><span>Nazwa · data</span><span>Pliki</span><span>Rozmiar</span><span>Akcje</span></div>
        <ul className="backup-list">{backups.map(backup => <li key={backup.id} className="backup-row">
          <div className="backup-name"><FaFileAlt aria-hidden="true"/><div><span className="backup-label">{backup.label || 'Kopia bez nazwy'}</span><time dateTime={backup.date}>{formatDate(backup.date)}</time></div></div>
          <span className="backup-file-count">{backup.fileCount ?? '?'}<span className="backup-mobile-label"> plików</span></span>
          <div className="backup-size"><span>{formatSize(backup.totalFileSize)}</span><small>ZIP {formatSize(backup.zipSize)}</small></div>
          <div className="backup-row-actions">
            <button className="backup-button backup-download-button" disabled={working || busy} onClick={() => handleDownload(backup)}><FaDownload aria-hidden="true"/>Pobierz</button>
            {!isReadOnly && <button className="backup-button backup-restore-button" disabled={working || busy} onClick={() => { if (!isReadOnly) setSelectedBackup(backup); }}><FaHistory aria-hidden="true"/>Przywróć</button>}
            <button className="backup-button backup-delete-button" disabled={working || busy} onClick={() => handleDelete(backup)}><FaTrash aria-hidden="true"/>Usuń</button>
          </div>
        </li>)}</ul>
      </>}
    {!isReadOnly && selectedBackup && <div className="backup-modal-overlay" onKeyDown={event => { if (event.key === 'Escape' && !working) setSelectedBackup(null); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="backup-restore-title" className="backup-modal">
        <h2 id="backup-restore-title">Przywrócić kopię zapasową?</h2>
        <p>{selectedBackup.label} · {formatDate(selectedBackup.date)}</p>
        <p>Wszystkie bieżące pieśni zostaną zastąpione plikami z tej kopii.</p>
        <div className="backup-modal-actions">
          <button autoFocus className="backup-button" disabled={working} onClick={() => setSelectedBackup(null)}>Anuluj</button>
          <button className="backup-button backup-restore-button" disabled={working || busy} onClick={handleRestore}>Przywróć kopię</button>
        </div>
      </div>
    </div>}
  </section>;
};
export default BackupManagerComponent;
